'use client';

import { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { ArrowLeft, TrendingUp, TrendingDown, Clock, Target, ShieldAlert, DollarSign, UserPlus, Check, Send, X, Heart, Share2, AlertTriangle, Lock, Pencil, History } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import SignalChartPlayer from '@/app/home-feed/components/SignalChartPlayer';
import IdeaTypeBadge from '@/components/common/IdeaTypeBadge';
import TradeEditModal from '@/app/home-feed/components/TradeEditModal';
import ShareSignalModal from '@/components/ShareSignalModal';
import { track } from '@/lib/analytics';
import { formatDateTime, parseServerDate } from '@/lib/dates';
import { TradeCardData, TradeDirection, Comment } from '@/app/home-feed/components/TradeCard';
import { uploadCommentImage } from '@/lib/commentImage';
import { CommentImageButton, CommentImageDraft, CommentImageView, makePasteHandler } from '@/app/home-feed/components/CommentImageParts';
import { useLang } from '@/lib/i18n/LanguageContext';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatPrice(price: number | null | undefined): string {
  // ANALYSIS ideas carry null levels — never crash on null.toLocaleString().
  if (price == null || !Number.isFinite(price)) return '—';
  return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 5 });
}

function calcRR(entry: number, sl: number, tp: number, direction: TradeDirection): number | null {
  const risk = direction === 'BUY' ? entry - sl : sl - entry;
  const reward = direction === 'BUY' ? tp - entry : entry - tp;
  if (risk <= 0 || reward <= 0) return null;
  return reward / risk;
}

function calcPoints(a: number, b: number): string {
  const diff = Math.abs(a - b);
  if (diff < 0.01) return diff.toFixed(5);
  if (diff < 1) return diff.toFixed(4);
  if (diff < 100) return diff.toFixed(2);
  return diff.toFixed(0);
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const supabase = createClient();
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) return { Authorization: `Bearer ${session.access_token}` };
    const { data: { session: refreshed } } = await supabase.auth.refreshSession();
    if (refreshed?.access_token) return { Authorization: `Bearer ${refreshed.access_token}` };
  } catch {}
  return {};
}

const timeframeLabels: Record<string, string> = {
  M1: '1m', M5: '5m', M15: '15m', M30: '30m', H1: '1H', H4: '4H', D1: 'D1',
};

// Field names in the public SL/TP change ledger → display labels
const REVISION_FIELD_LABELS: Record<string, string> = {
  stopLoss: 'Stop Loss',
  takeProfit1: 'Take Profit 1',
  takeProfit2: 'Take Profit 2',
  takeProfit3: 'Take Profit 3',
};

const marketColors: Record<string, { bg: string; text: string; border: string }> = {
  FOREX:       { bg: '#e7ecfa', text: '#3e5fbf', border: 'transparent' },
  CRYPTO:      { bg: '#f7eeda', text: '#b07c1f', border: 'transparent' },
  STOCKS:      { bg: '#e1f3e8', text: '#0e8a4d', border: 'transparent' },
  INDICES:     { bg: '#e7ecfa', text: '#3e5fbf', border: 'transparent' },
  COMMODITIES: { bg: '#f7eeda', text: '#b07c1f', border: 'transparent' },
};

// ─── R:R Breakdown ───────────────────────────────────────────────────────────

function RRBreakdown({ trade }: { trade: TradeCardData }) {
  const { t } = useLang();
  const { entryPrice, stopLoss, takeProfit1, takeProfit2, takeProfit3, direction } = trade;
  const isBuy = direction === 'BUY';
  const risk = isBuy ? entryPrice - stopLoss : stopLoss - entryPrice;
  const riskPct = entryPrice > 0 ? ((risk / entryPrice) * 100).toFixed(2) : '0.00';
  const tps = [
    { label: 'TP1', price: takeProfit1 },
    ...(takeProfit2 ? [{ label: 'TP2', price: takeProfit2 }] : []),
    ...(takeProfit3 ? [{ label: 'TP3', price: takeProfit3 }] : []),
  ];
  const bestTP = tps[tps.length - 1];
  const bestRR = calcRR(entryPrice, stopLoss, bestTP.price, direction);

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
      <div className="px-4 py-2.5 border-b" style={{ borderColor: 'var(--border)' }}>
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>{t('R:R Breakdown')}</span>
      </div>
      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: 'var(--bearish)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{t('Risk')}</span>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Entry {isBuy ? '−' : '+'} SL</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono-nums text-sm font-semibold" style={{ color: 'var(--bearish)' }}>{calcPoints(entryPrice, stopLoss)} pts</span>
            <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'var(--bearish-muted)', color: 'var(--bearish)' }}>{riskPct}%</span>
          </div>
        </div>
        {tps.map(({ label, price }) => {
          const rr = calcRR(entryPrice, stopLoss, price, direction);
          const reward = isBuy ? price - entryPrice : entryPrice - price;
          const rewardPct = entryPrice > 0 ? ((reward / entryPrice) * 100).toFixed(2) : '0.00';
          return (
            <div key={label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ background: 'var(--bullish)' }} />
                <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{t('Reward')} {label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono-nums text-sm font-semibold" style={{ color: 'var(--bullish)' }}>{calcPoints(price, entryPrice)} pts</span>
                <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'var(--bullish-muted)', color: 'var(--bullish)' }}>{rewardPct}%</span>
                {rr !== null && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded" style={{
                    background: rr >= 2 ? 'var(--bullish-muted)' : rr >= 1 ? 'var(--win-muted)' : 'var(--bearish-muted)',
                    color: rr >= 2 ? 'var(--bullish)' : rr >= 1 ? 'var(--win)' : 'var(--bearish)',
                  }}>1:{rr.toFixed(1)}</span>
                )}
              </div>
            </div>
          );
        })}
        {bestRR !== null && (
          <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t('Best R:R')} ({bestTP.label})</span>
            <span className="font-mono-nums font-bold text-base px-3 py-1 rounded-lg" style={{
              background: bestRR >= 2 ? 'var(--bullish-muted)' : bestRR >= 1 ? 'var(--win-muted)' : 'var(--bearish-muted)',
              color: bestRR >= 2 ? 'var(--bullish)' : bestRR >= 1 ? 'var(--win)' : 'var(--bearish)',
            }}>1:{bestRR.toFixed(2)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Price Levels Section ────────────────────────────────────────────────────

function PriceLevels({ trade }: { trade: TradeCardData }) {
  const { t } = useLang();
  const isBuy = trade.direction === 'BUY';
  const levels = [
    { label: 'Entry', value: trade.entryPrice, color: 'var(--win)', icon: <DollarSign size={13} /> },
    { label: 'Stop Loss', value: trade.stopLoss, color: 'var(--bearish)', icon: <ShieldAlert size={13} /> },
    { label: 'TP1', value: trade.takeProfit1, color: 'var(--bullish)', icon: <Target size={13} /> },
    ...(trade.takeProfit2 ? [{ label: 'TP2', value: trade.takeProfit2, color: 'var(--bullish)', icon: <Target size={13} /> }] : []),
    ...(trade.takeProfit3 ? [{ label: 'TP3', value: trade.takeProfit3, color: 'var(--bullish)', icon: <Target size={13} /> }] : []),
  ];

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
      <div className="px-4 py-2.5 border-b" style={{ borderColor: 'var(--border)' }}>
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>{t('Price Levels')}</span>
      </div>
      <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {levels.map((level) => (
          <div key={level.label} className="rounded-xl p-3 text-center" style={{ background: 'var(--bg-base)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-center gap-1 mb-1.5" style={{ color: level.color }}>
              {level.icon}
              <span className="text-xs font-semibold">{level.label}</span>
            </div>
            <div className="text-sm font-mono-nums font-bold" style={{ color: level.color }}>{formatPrice(level.value)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Signal Pills ────────────────────────────────────────────────────────────

function SignalSection({ trade }: { trade: TradeCardData }) {
  const { t } = useLang();
  const { entryPrice, stopLoss, takeProfit1, takeProfit2, takeProfit3, direction, status } = trade;
  const isBuy = direction === 'BUY';
  const risk = isBuy ? entryPrice - stopLoss : stopLoss - entryPrice;
  const rr1 = risk > 0 ? ((isBuy ? takeProfit1 - entryPrice : entryPrice - takeProfit1) / risk).toFixed(1) : null;
  const rr2 = takeProfit2 && risk > 0 ? ((isBuy ? takeProfit2 - entryPrice : entryPrice - takeProfit2) / risk).toFixed(1) : null;
  const rr3 = takeProfit3 && risk > 0 ? ((isBuy ? takeProfit3 - entryPrice : entryPrice - takeProfit3) / risk).toFixed(1) : null;

  const pills = [
    { label: isBuy ? '▲ BUY' : '▼ SELL', sublabel: 'Entry Zone', price: entryPrice, color: isBuy ? 'var(--bullish)' : 'var(--bearish)', bg: isBuy ? 'var(--bullish-muted)' : 'var(--bearish-muted)', rr: null },
    { label: '✕ STOP', sublabel: 'Invalidation', price: stopLoss, color: 'var(--bearish)', bg: 'var(--bearish-muted)', rr: null },
    { label: '◎ TP1', sublabel: 'Target 1', price: takeProfit1, color: 'var(--bullish)', bg: 'var(--bullish-muted)', rr: rr1 },
    ...(takeProfit2 ? [{ label: '◎ TP2', sublabel: 'Target 2', price: takeProfit2, color: 'var(--bullish)', bg: 'var(--bullish-muted)', rr: rr2 }] : []),
    ...(takeProfit3 ? [{ label: '◎ TP3', sublabel: 'Target 3', price: takeProfit3, color: 'var(--bullish)', bg: 'var(--bullish-muted)', rr: rr3 }] : []),
  ];

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
      <div className="px-4 py-2.5 border-b" style={{ borderColor: 'var(--border)' }}>
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>{t('Entry / Exit Signals')}</span>
      </div>
      <div className="p-4">
        <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          {pills.map((p) => (
            <div key={p.label} className="flex flex-col items-center gap-1 px-4 py-3 rounded-xl shrink-0" style={{ background: p.bg, border: '1px solid transparent', minWidth: 100 }}>
              <span className="text-xs font-semibold" style={{ color: p.color }}>{p.label}</span>
              <span className="font-mono-nums text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{formatPrice(p.price)}</span>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t(p.sublabel)}</span>
              {p.rr && <span className="text-xs font-semibold" style={{ color: p.color }}>1:{p.rr}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Comments Section ────────────────────────────────────────────────────────

function CommentsSection({ tradeId, initialComments }: { tradeId: string; initialComments: Comment[] }) {
  const { user } = useAuth();
  const { t } = useLang();
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const imagePreview = useMemo(() => (image ? URL.createObjectURL(image) : null), [image]);
  useEffect(() => {
    return () => { if (imagePreview) URL.revokeObjectURL(imagePreview); };
  }, [imagePreview]);

  // Anchor to ?comment=<id> (from a comment/reply/mention notification): scroll + highlight.
  useEffect(() => {
    if (loading || comments.length === 0) return;
    const target = new URLSearchParams(window.location.search).get('comment');
    if (!target) return;
    const el = document.getElementById(`comment-${target}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightId(target);
    const t = setTimeout(() => setHighlightId(null), 2600);
    return () => clearTimeout(t);
  }, [loading, comments]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/trades/${tradeId}/comments`);
        if (res.ok) {
          const data = await res.json();
          setComments(data.comments || []);
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [tradeId]);

  async function handlePost(e: React.FormEvent) {
    e.preventDefault();
    if ((!text.trim() && !image) || posting) return;
    const content = text.trim();
    setPosting(true);
    try {
      // Upload the attached chart first (compressed client-side, moderated server-side).
      let imageUrl: string | undefined;
      if (image) imageUrl = await uploadCommentImage(image);

      const authHeaders = await getAuthHeaders();
      const res = await fetch(`/api/trades/${tradeId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({ content, imageUrl }),
      });
      if (res.ok) {
        const data = await res.json();
        setComments((prev) => [data.comment, ...prev]);
        setText('');
        setImage(null);
        toast.success(t('Comment posted'));
      } else {
        const err = await res.json();
        toast.error(err.error || t('Failed to post comment'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('Network error'));
    }
    setPosting(false);
  }

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
      <div className="px-4 py-2.5 border-b" style={{ borderColor: 'var(--border)' }}>
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
          {t('Discussion')} {comments.length > 0 && <span style={{ color: 'var(--text-muted)' }}>({comments.length})</span>}
        </span>
      </div>
      <div className="p-4">
        {user && (
          <form onSubmit={handlePost} className="flex gap-2 mb-4">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
              style={{ background: 'var(--accent)', color: 'var(--bg-base)' }}>
              {(user.user_metadata?.username || user.email || 'U')[0].toUpperCase()}
            </div>
            <div className="flex-1">
              {imagePreview && (
                <CommentImageDraft preview={imagePreview} uploading={posting} onRemove={() => setImage(null)} />
              )}
              <div className="flex gap-2">
                <input type="text" value={text} onChange={(e) => setText(e.target.value)}
                  onPaste={makePasteHandler(setImage)}
                  placeholder={t('Add your analysis…')} className="input-base flex-1 px-3 py-2 text-sm" />
                <CommentImageButton disabled={posting} onPick={setImage} />
                <button type="submit" disabled={(!text.trim() && !image) || posting}
                  className="btn-primary px-3 py-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1">
                  <Send size={12} />{posting ? t('Posting…') : t('Post')}
                </button>
              </div>
            </div>
          </form>
        )}
        {loading ? (
          <div className="flex flex-col gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-2 animate-pulse">
                <div className="w-7 h-7 rounded-full shrink-0" style={{ background: 'var(--bg-sunken)' }} />
                <div className="flex-1 space-y-2">
                  <div className="h-3 rounded w-24" style={{ background: 'var(--bg-sunken)' }} />
                  <div className="h-3 rounded w-full" style={{ background: 'var(--bg-sunken)' }} />
                </div>
              </div>
            ))}
          </div>
        ) : comments.length === 0 ? (
          <p className="text-xs text-center py-6" style={{ color: 'var(--text-muted)' }}>{t('No comments yet. Be the first to share your analysis.')}</p>
        ) : (
          <div className="flex flex-col gap-4">
            {comments.map((c) => (
              <div
                key={c.id}
                id={`comment-${c.id}`}
                className="flex gap-2 rounded-xl transition-all duration-500"
                style={highlightId === c.id
                  ? { background: 'var(--accent-muted)', boxShadow: '0 0 0 1px var(--accent)', padding: 8, margin: -8 }
                  : undefined}
              >
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}>
                  {c.authorInitial}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Link href={`/profile/${c.author}`} className="text-xs font-semibold hover:underline" style={{ color: 'var(--text-primary)' }}>
                      @{c.author}
                    </Link>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {parseServerDate(c.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  {c.content && (
                    <p className="text-sm mt-0.5 leading-relaxed" style={{ color: 'var(--text-body)' }}>{c.content}</p>
                  )}
                  {c.imageUrl && <CommentImageView url={c.imageUrl} />}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

// Client idea page. `initialTrade` (from the server page.tsx, anonymous read) seeds the view so the
// h1 / author / levels / thesis are in the SSR HTML; the /api/trades/[id] refetch on mount still
// runs to pick up viewer-specific fields (isLiked, isFollowing, block filtering) and to count the view.
export default function IdeaDetailPage({ id, initialTrade }: { id: string; initialTrade?: TradeCardData | null }) {
  const router = useRouter();
  const { user } = useAuth();
  const { t } = useLang();

  const [trade, setTrade] = useState<TradeCardData | null>(initialTrade ?? null);
  const [loading, setLoading] = useState(!initialTrade);
  const [error, setError] = useState<string | null>(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [liked, setLiked] = useState(initialTrade?.isLiked ?? false);
  const [likeCount, setLikeCount] = useState(initialTrade?.likeCount ?? 0);
  const [following, setFollowing] = useState(initialTrade?.author.isFollowing ?? false);
  const [followLoading, setFollowLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);


  useEffect(() => {
    if (!id) return;
    const load = async () => {
      // With server-seeded data there is nothing to hide behind a spinner — refresh in place.
      if (!initialTrade) setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/trades/${id}`);
        if (!res.ok) {
          if (!initialTrade || res.status === 404 || res.status === 403) setError(t('Trade idea not found'));
          setLoading(false);
          return;
        }
        const data = await res.json();
        const td: TradeCardData = data.trade;
        setTrade(td);
        track('idea_view', { item_id: td.id, author_id: td.author.userId, market: td.market, direction: td.direction });
        // Count a real view once per browser session (viewCount is settlement-locked;
        // increment_trade_view is a SECURITY DEFINER RPC that bypasses the column grant).
        try {
          const vk = `smt_viewed_${td.id}`;
          if (typeof window !== 'undefined' && !sessionStorage.getItem(vk)) {
            sessionStorage.setItem(vk, '1');
            createClient().rpc('increment_trade_view', { p_trade_id: td.id }).then(() => {}, () => {});
          }
        } catch { /* view counting is best-effort */ }
        setLiked(td.isLiked);
        setLikeCount(td.likeCount);
        setFollowing(td.author.isFollowing);
      } catch {
        if (!initialTrade) setError(t('Failed to load trade idea'));
      }
      setLoading(false);
    };
    load();
    // initialTrade only seeds state / the fallback branch; it doesn't change for a mounted page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleLike() {
    if (!trade) return;
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikeCount((p) => wasLiked ? p - 1 : p + 1);
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`/api/trades/${trade.id}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setLikeCount(data.likeCount);
        setLiked(data.action === 'liked');
      } else {
        setLiked(wasLiked);
        setLikeCount((p) => wasLiked ? p + 1 : p - 1);
      }
    } catch {
      setLiked(wasLiked);
      setLikeCount((p) => wasLiked ? p + 1 : p - 1);
    }
  }

  async function handleFollow() {
    if (!trade || followLoading) return;
    setFollowLoading(true);
    const newFollowing = !following;
    setFollowing(newFollowing);
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`/api/users/${encodeURIComponent(trade.author.username)}/follow`, {
        method: 'POST',
        headers: { ...authHeaders },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setFollowing(data.following);
        if (data.following && trade.author.userId) track('follow_trader', { target_id: trade.author.userId });
        toast.success(data.following ? `${t('Now following')} @${trade.author.username}` : `${t('Unfollowed')} @${trade.author.username}`);
      } else if (res.status === 401) {
        setFollowing(!newFollowing);
        toast.error(t('Please sign in to follow traders'));
      } else {
        setFollowing(!newFollowing);
      }
    } catch {
      setFollowing(!newFollowing);
    } finally {
      setFollowLoading(false);
    }
  }

  function handleShare() {
    // Extend the Share button into the full Share-as-Signal modal.
    setShareOpen(true);
  }

  // Auto-open the share modal right after publishing with "Share as signal" checked (?share=1).
  useEffect(() => {
    if (!trade) return;
    if (new URLSearchParams(window.location.search).get('share') === '1') {
      setShareOpen(true);
    }
  }, [trade]);

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-base)' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full animate-spin" style={{ border: '2px solid var(--accent)', borderTopColor: 'transparent' }} />
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('Loading idea…')}</p>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error || !trade) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg-base)' }}>
        <div className="text-center">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--bearish-muted)', border: '1px solid var(--bearish)' }}>
            <AlertTriangle size={24} style={{ color: 'var(--bearish)' }} />
          </div>
          <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{t('Idea Not Found')}</h2>
          <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>{error || t('This trade idea does not exist or has been removed.')}</p>
          <Link href="/home-feed" className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2">
            <ArrowLeft size={14} /> {t('Back to Feed')}
          </Link>
        </div>
      </div>
    );
  }

  const isBuy = trade.direction === 'BUY';
  const isAnalysis = trade.ideaType === 'ANALYSIS'; // no actionable levels → hide signal/price blocks
  const isOwner = !!(user?.id && trade.author.userId && user.id === trade.author.userId);
  const marketColor = marketColors[trade.market] ?? { bg: '#e7ecfa', text: '#3e5fbf', border: 'transparent' };
  const resolvedMediaUrl = trade.imageUrl || trade.chartImage || trade.thumbnailUrl || undefined;

  // Border-only status pill per the warm-light redesign: transparent bg, colored 1.5px border,
  // colored text — same convention as TradeCard's statusConfig.
  const statusConfig: Record<string, { label: string; color: string; bg: string; border: string; dot: string }> = {
    OPEN:  { label: 'OPEN',    color: 'var(--info)',    bg: 'transparent', border: 'var(--info)',    dot: '●' },
    WIN:   { label: 'HIT TP',  color: 'var(--bullish)', bg: 'transparent', border: 'var(--bullish)', dot: '✓' },
    LOSS:  { label: 'HIT SL',  color: 'var(--bearish)', bg: 'transparent', border: 'var(--bearish)', dot: '✗' },
    BE:    { label: 'CLOSED',  color: 'var(--win)',     bg: 'transparent', border: 'var(--win)',     dot: '~' },
  };
  const sc = statusConfig[trade.status] ?? statusConfig.OPEN;

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-base)' }}>
      {/* ── Main Content (container: max-width 1020, matches site-wide layout unifikacija) ── */}
      <div className="flex flex-col gap-6" style={{ maxWidth: 1020, margin: '0 auto', padding: '20px 24px 70px' }}>

        {/* ── Breadcrumb row ── */}
        <div className="flex items-center flex-wrap" style={{ padding: '6px 0 18px', gap: 14 }}>
          <button
            onClick={() => {
              // In-app history (came from a feed card) → real back, preserving the feed scroll.
              // Opened directly (shared link / new tab) → back would do nothing; go to the feed.
              if (window.history.length > 1) router.back();
              else router.push('/home-feed');
            }}
            title={t('Back to feed')}
            className="flex items-center gap-1.5 transition-colors duration-150"
            style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-secondary)' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
          >
            <ArrowLeft size={15} />
            <span>{t('Feed')}</span>
          </button>

          <span className="font-mono-nums font-extrabold" style={{ fontSize: 18, color: 'var(--text-primary)', letterSpacing: '0.03em' }}>{trade.ticker}</span>

          <span className={isBuy ? 'badge-long' : 'badge-short'} style={{ fontSize: 12, fontWeight: 800, borderRadius: 999, padding: '4px 11px' }}>
            {isBuy ? 'LONG' : 'SHORT'}
          </span>

          <span style={{ fontSize: 12, fontWeight: 800, borderRadius: 999, padding: '4px 11px', border: `1.5px solid ${sc.border}`, color: sc.color, background: 'transparent' }}>
            {sc.dot} {sc.label}
          </span>

          <div className="flex-1" />

          {isOwner && trade.status === 'WIN' ? (
            // The author's own winning signal → the share button becomes the "share your win" CTA.
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5"
              style={{ background: 'var(--bullish)', color: '#fff', borderRadius: 11, padding: '9px 16px', fontSize: 13.5, fontWeight: 800 }}
            >
              <Share2 size={14} />
              <span>{t('Share your win 🏆')}</span>
            </button>
          ) : (
          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 transition-colors duration-150"
            style={{ background: 'var(--bg-sunken)', color: 'var(--text-secondary)', borderRadius: 11, padding: '9px 16px', fontSize: 13.5, fontWeight: 700 }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)'; }}
          >
            <Share2 size={14} />
            <span>{t('Share')}</span>
          </button>
          )}
        </div>

        {/* ── Hero Header ── */}
        <div className="rounded-[20px] overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          {/* Instrument + badges */}
          <div className="p-6 pb-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {/* The page's single h1: ticker visible, direction/market for crawlers only (visually
                      identical to the former span — Tailwind preflight resets h1 size/margin). */}
                  <h1 className="font-mono-nums font-black text-2xl tracking-wider m-0" style={{ color: 'var(--text-primary)', letterSpacing: '0.06em' }}>
                    {trade.ticker}
                    <span className="sr-only"> {isBuy ? 'LONG' : 'SHORT'} · {trade.market}</span>
                  </h1>
                  {/* IdeaTypeBadge is the type marker; direction (bullish/bearish bias) stays shown for
                      analyses too — the composer collects it as a required field for every idea. */}
                  <IdeaTypeBadge ideaType={trade.ideaType} size="md" />
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-bold ${isBuy ? 'badge-long' : 'badge-short'}`}>
                    {isBuy ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    {isBuy ? 'LONG' : 'SHORT'}
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-xs font-extrabold" style={{ background: sc.bg, color: sc.color, border: `1.5px solid ${sc.border}`, letterSpacing: '0.4px' }}>
                    {sc.dot} {sc.label}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2.5 py-1 rounded-lg text-xs font-semibold" style={{ background: marketColor.bg, color: marketColor.text, border: `1px solid ${marketColor.border}` }}>
                    {trade.market}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium" style={{ background: 'var(--bg-sunken)', color: 'var(--text-secondary)', border: '1px solid transparent' }}>
                    <Clock size={10} />{timeframeLabels[trade.timeframe] ?? trade.timeframe}
                  </span>
                  <Link
                    href={`/live?room=${encodeURIComponent(trade.ticker)}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold"
                    style={{ background: 'var(--accent-muted)', color: 'var(--accent)', border: '1px solid transparent' }}
                    title={`Open ${trade.ticker} live trading chat`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: 'var(--bullish)' }} />
                    {t('Live trading')} · {trade.ticker}
                  </Link>
                </div>
              </div>

            </div>
          </div>

          {/* Author row */}
          <div className="px-6 pb-5 flex items-center justify-between gap-3 flex-wrap border-t pt-4" style={{ borderColor: 'var(--border-subtle)' }}>
            <Link href={`/profile/${trade.author.username}`} className="flex items-center gap-3 group">
              <div className="w-[46px] h-[46px] rounded-full flex items-center justify-center text-base font-bold shrink-0 overflow-hidden"
                style={{ background: trade.author.avatarUrl ? undefined : 'var(--accent)', color: 'var(--bg-base)' }}>
                {trade.author.avatarUrl ? (
                  <img src={trade.author.avatarUrl} alt={trade.author.displayName} className="w-full h-full object-cover" />
                ) : trade.author.initial}
              </div>
              <div>
                <div className="font-semibold text-sm group-hover:underline" style={{ color: 'var(--text-primary)' }}>{trade.author.displayName}</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>@{trade.author.username}</div>
              </div>
            </Link>
            <div className="flex items-center gap-3 flex-wrap">
              {trade.author.winRate != null && trade.author.totalTrades != null && trade.author.totalTrades >= 3 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold" style={{ color: trade.author.winRate >= 60 ? 'var(--bullish)' : trade.author.winRate >= 40 ? 'var(--win)' : 'var(--bearish)' }}>
                    {trade.author.winRate}% WR
                  </span>
                  <span className="text-xs" style={{ color: 'var(--text-dim)' }}>·</span>
                  <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{trade.author.totalTrades} {t('trades')}</span>
                </div>
              )}
              <div className="text-xs" style={{ color: 'var(--text-muted)' }} suppressHydrationWarning>
                {formatDateTime(trade.createdAt, { alwaysYear: true })}
              </div>
              {!isOwner && (
                <button
                  onClick={handleFollow}
                  disabled={followLoading}
                  className={following ? 'btn-ghost flex items-center gap-1.5 px-3 py-1.5 text-xs disabled:opacity-60' : 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 disabled:opacity-60'}
                  style={following ? undefined : { background: 'var(--accent-muted)', color: 'var(--accent)', border: '1px solid transparent' }}
                >
                  {following ? <Check size={12} /> : <UserPlus size={12} />}
                  {following ? t('Following') : t('Follow')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Chart Section ── */}
        <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          <div className="border-b" style={{ padding: '16px 22px', borderColor: 'var(--border-subtle)' }}>
            <span className="uppercase" style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '1.2px', color: 'var(--text-muted)' }}>{t('Chart')}</span>
          </div>
          <div style={{ padding: '20px 22px' }}>
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-subtle)', background: 'var(--bg-card)' }}>
              <SignalChartPlayer trade={trade} height={400} allowTfSwitch />
            </div>
          </div>
        </div>

        {/* ── Thesis / Setup Description ── */}
        {(trade.caption || isOwner) && (
          <div className="rounded-[20px] overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <div className="px-5 py-2.5 border-b flex items-center justify-between gap-3" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>{t('Setup Description / Thesis')}</span>
                {trade.editedAt && (
                  <span className="text-xs italic" style={{ color: 'var(--text-muted)' }} title={`Edited ${parseServerDate(trade.editedAt).toLocaleString()}`}>{t('edited')}</span>
                )}
              </div>
              {isOwner && (
                <button
                  onClick={() => setEditModalOpen(true)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors duration-150"
                  style={{ background: 'var(--accent-muted)', color: 'var(--accent)', border: '1px solid transparent' }}
                >
                  <Pencil size={11} /> {t('Edit')}
                </button>
              )}
            </div>
            <div className="p-5">
              {trade.caption ? (
                <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--text-body)' }}>{trade.caption}</p>
              ) : (
                <p className="text-sm italic" style={{ color: 'var(--text-muted)' }}>{t('No commentary yet — click Edit to add your thesis.')}</p>
              )}
            </div>
          </div>
        )}

        {/* ── Key Trade Data Grid ── */}
        <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          <div className="border-b flex items-center justify-between gap-3" style={{ padding: '16px 22px', borderColor: 'var(--border-subtle)' }}>
            <span className="uppercase" style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '1.2px', color: 'var(--text-muted)' }}>{t('Trade Details')}</span>
            <div className="flex items-center gap-3 flex-wrap justify-end">
              {/* Live signal: SL/TP may move, but only through the public ledger (owner rule 2026-09-02) */}
              {isOwner && !isAnalysis && (trade.status === 'OPEN' || trade.status === 'PENDING') && (
                <button
                  onClick={() => setEditModalOpen(true)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors duration-150"
                  style={{ background: 'var(--accent-muted)', color: 'var(--accent)', border: '1px solid transparent' }}
                >
                  <Pencil size={11} /> {t('Edit SL/TP')}
                </button>
              )}
              <span className="inline-flex items-center gap-1.5" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                <Lock size={12} />
                {!isAnalysis && (trade.status === 'OPEN' || trade.status === 'PENDING')
                  ? t('Entry locked · SL/TP changes are recorded publicly')
                  : t("Locked — verified entry, can't be changed")}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3" style={{ padding: 22, gap: '22px 16px' }}>
            {[
              { label: t('Instrument'), value: trade.ticker, color: 'var(--text-primary)' },
              { label: t('Direction'), value: isBuy ? '▲ LONG' : '▼ SHORT', color: isBuy ? 'var(--bullish)' : 'var(--bearish)' },
              { label: t('Market'), value: trade.market, color: marketColor.text },
              { label: t('Timeframe'), value: timeframeLabels[trade.timeframe] ?? trade.timeframe, color: 'var(--info)' },
              // Price levels + R:R exist only on SIGNAL ideas; ANALYSIS ideas carry null levels.
              ...(!isAnalysis ? [
                { label: 'Entry Price', value: formatPrice(trade.entryPrice), color: 'var(--win)' },
                { label: 'Stop Loss', value: formatPrice(trade.stopLoss), color: 'var(--bearish)' },
                { label: 'Take Profit 1', value: formatPrice(trade.takeProfit1), color: 'var(--bullish)' },
                ...(trade.takeProfit2 ? [{ label: 'Take Profit 2', value: formatPrice(trade.takeProfit2), color: 'var(--bullish)' }] : []),
                ...(trade.takeProfit3 ? [{ label: 'Take Profit 3', value: formatPrice(trade.takeProfit3), color: 'var(--bullish)' }] : []),
              ] : []),
              { label: t('Status'), value: `${sc.dot} ${sc.label}`, color: sc.color },
              ...(!isAnalysis ? [{
                label: t('Best R:R'),
                value: (() => {
                  const bestTP = trade.takeProfit3 || trade.takeProfit2 || trade.takeProfit1;
                  const rr = calcRR(trade.entryPrice, trade.stopLoss, bestTP, trade.direction);
                  return rr ? `1:${rr.toFixed(2)}` : '—';
                })(),
                color: 'var(--accent)',
              }] : []),
              {
                label: t('Published'),
                value: formatDateTime(trade.createdAt, { alwaysYear: true }),
                color: 'var(--text-secondary)',
              },
            ].map((item) => (
              <div key={item.label} className="flex flex-col gap-1">
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{item.label}</span>
                {/* "Published" is formatted in the viewer's timezone — server (UTC) and browser may differ. */}
                <span className="font-mono-nums" style={{ fontSize: 16, fontWeight: 800, color: item.color }} suppressHydrationWarning>{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Change history — public SL/TP ledger (transparency: every move, old → new, with reason) ── */}
        {!isAnalysis && (trade.revisions?.length ?? 0) > 0 && (
          <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <div className="border-b flex items-center gap-2 flex-wrap" style={{ padding: '16px 22px', borderColor: 'var(--border-subtle)' }}>
              <History size={14} style={{ color: 'var(--win)' }} />
              <span className="uppercase" style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '1.2px', color: 'var(--text-muted)' }}>{t('Change history')}</span>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                · {t('every SL/TP change is public and applies only from the moment it was made')}
              </span>
            </div>
            <ol className="flex flex-col" style={{ padding: '4px 22px 12px' }}>
              {trade.revisions!.map((rev) => (
                <li key={rev.id} className="py-3 border-b last:border-b-0" style={{ borderColor: 'var(--border-subtle)' }}>
                  <span className="font-mono-nums" style={{ fontSize: 12.5, color: 'var(--text-secondary)' }} suppressHydrationWarning>
                    {formatDateTime(rev.createdAt, { alwaysYear: true })}
                  </span>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1.5">
                    {rev.changes.map((c, i) => (
                      <span key={i} className="font-mono-nums" style={{ fontSize: 13.5, fontWeight: 700, color: c.field === 'stopLoss' ? 'var(--bearish)' : 'var(--bullish)' }}>
                        {REVISION_FIELD_LABELS[c.field] ?? c.field}:{' '}
                        <span style={{ color: 'var(--text-muted)', textDecoration: 'line-through', fontWeight: 600 }}>
                          {c.from != null ? formatPrice(c.from) : '—'}
                        </span>
                        {' → '}
                        {c.to != null ? formatPrice(c.to) : '—'}
                      </span>
                    ))}
                  </div>
                  {rev.note ? (
                    <p className="mt-1.5" style={{ fontSize: 13, color: 'var(--text-body)' }}>„{rev.note}”</p>
                  ) : (
                    <p className="mt-1.5 italic" style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{t('No reason given')}</p>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* ── Signal / price blocks — SIGNAL ideas only (ANALYSIS carries null levels) ── */}
        {!isAnalysis && (
          <>
            {/* ── Signal Section ── */}
            <SignalSection trade={trade} />

            {/* ── Price Levels ── */}
            <PriceLevels trade={trade} />

            {/* ── R:R Breakdown ── */}
            <RRBreakdown trade={trade} />
          </>
        )}

        {/* ── Social Proof + Actions ── */}
        <div className="rounded-[20px] overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          <div className="p-4 flex items-center justify-between gap-4 flex-wrap">
            {/* Like + share */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleLike}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm transition-all duration-150"
                style={{ color: liked ? 'var(--bearish)' : 'var(--text-primary)', background: liked ? 'var(--bearish-muted)' : 'transparent', border: '1px solid transparent' }}
              >
                <Heart size={16} fill={liked ? 'var(--bearish)' : 'none'} />
                <span className="text-xs font-mono-nums font-semibold">{likeCount}</span>
              </button>
              <button
                onClick={handleShare}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm transition-all duration-150"
                style={{ color: 'var(--text-primary)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                <Share2 size={16} />
              </button>
              <button
                onClick={() => router.push(`/inbox/new?trade=${id}`)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm transition-all duration-150"
                style={{ color: 'var(--text-primary)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                title={t('Send this idea via direct message')}
              >
                <Send size={16} />
                <span className="text-xs font-semibold">DM</span>
              </button>
            </div>

          </div>
        </div>

        {/* ── Comments ── */}
        <CommentsSection tradeId={trade.id} initialComments={trade.comments} />

        {/* Bottom padding */}
        <div style={{ height: 32 }} />
      </div>

      {/* ── Image Lightbox ── */}
      {imageOpen && resolvedMediaUrl && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(12px)' }}
          onClick={() => setImageOpen(false)}
        >
          <img
            src={resolvedMediaUrl}
            alt={`${trade.ticker} chart analysis`}
            className="max-w-full max-h-full rounded-xl object-contain"
            style={{ maxWidth: 'min(95vw, 1400px)', maxHeight: '90vh', boxShadow: '0 32px 80px rgba(0,0,0,0.9)', display: 'block' }}
            loading="lazy"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-150"
            style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.15)' }}
            onClick={() => setImageOpen(false)}
          >
            <X size={18} />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <span className="text-sm font-black font-mono-nums" style={{ color: '#f1f5f9', letterSpacing: '0.06em' }}>{trade.ticker}</span>
            <span className="text-xs" style={{ color: '#9ca3af' }}>·</span>
            <span className="text-xs font-bold" style={{ color: isBuy ? '#4ade80' : '#f87171' }}>{isBuy ? '▲ LONG' : '▼ SHORT'}</span>
          </div>
        </div>
      )}

      {/* ── Edit caption modal (owner only) ── */}
      {isOwner && (
        <TradeEditModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          trade={trade}
          onTradeUpdated={(updated) => setTrade((prev) => (prev ? { ...prev, ...updated } : prev))}
        />
      )}

      {/* ── Share as Signal modal ── */}
      <ShareSignalModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        tradeId={trade.id}
        ticker={trade.ticker}
        direction={trade.direction}
        status={trade.status}
      />
    </div>
  );
}
